import React, { useEffect, useState, useRef, Component } from 'react';
import { SearchIcon, FilterIcon, GlobeIcon, BuildingIcon, UsersIcon, MapPinIcon, BriefcaseIcon, ExternalLinkIcon, ShareIcon, TwitterIcon, FacebookIcon, CopyIcon, CheckIcon, XIcon, ChevronRightIcon, ChevronLeftIcon, ImageIcon } from 'lucide-react';
// Company data structure
interface Company {
  name: string;
  description: string;
  primaryIndustry: string;
  size: string;
  type: string;
  location: string;
  country: string;
  domain: string;
  linkedinUrl: string;
  logoUrl: string;
}
export const Companies = ({
  onCursorChange
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [selectedSize, setSelectedSize] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [activeCompany, setActiveCompany] = useState<Company | null>(null);
  const [highlightedCompany, setHighlightedCompany] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [industries, setIndustries] = useState<string[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [showShareOptions, setShowShareOptions] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalCompany, setModalCompany] = useState<Company | null>(null);
  const [logoErrors, setLogoErrors] = useState<Record<string, boolean>>({});
  const companiesRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 6;
  // Initialize company data
  useEffect(() => {
    const companyData: Company[] = [{
      name: 'Google',
      description: "A problem isn't truly solved until it's solved for all. Googlers build products that help create opportunities for everyone, whether down the street or across the globe. Bring your insight, imagination and a healthy disregard for the impossible. Bring everything that makes you unique. Together, we can build for everyone.",
      primaryIndustry: 'Software Development',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Mountain View, CA',
      country: 'United States',
      domain: 'google.com',
      linkedinUrl: 'https://www.linkedin.com/company/google',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg'
    }, {
      name: 'Amazon',
      description: 'Amazon is guided by four principles: customer obsession rather than competitor focus, passion for invention, commitment to operational excellence, and long-term thinking. We are driven by the excitement of building technologies, inventing products, and providing services that change lives.',
      primaryIndustry: 'Software Development',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Seattle, WA',
      country: 'United States',
      domain: 'amazon.com',
      linkedinUrl: 'https://www.linkedin.com/company/amazon',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg'
    }, {
      name: 'LinkedIn',
      description: "Founded in 2003, LinkedIn connects the world's professionals to make them more productive and successful. With more than 1 billion members worldwide, including executives from every Fortune 500 company, LinkedIn is the world's largest professional network.",
      primaryIndustry: 'Software Development',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Sunnyvale, CA',
      country: 'United States',
      domain: 'linkedin.com',
      linkedinUrl: 'https://www.linkedin.com/company/linkedin',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png'
    }, {
      name: 'Microsoft',
      description: "Every company has a mission. What's ours? To empower every person and every organization to achieve more. We believe technology can and should be a force for good and that meaningful innovation contributes to a brighter world in the future and today.",
      primaryIndustry: 'Software Development',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Redmond, Washington',
      country: 'United States',
      domain: 'microsoft.com',
      linkedinUrl: 'https://www.linkedin.com/company/microsoft',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg'
    }, {
      name: 'TED Conferences',
      description: "TED's mission is to discover and champion the ideas that will shape tomorrow. Powerful ideas, powerfully presented, can move us to feel something, to think differently, to take action and create a brighter future.",
      primaryIndustry: 'Non-profit Organizations',
      size: '51-200 employees',
      type: 'Non Profit',
      location: 'New York, NY',
      country: 'United States',
      domain: 'ted.com',
      linkedinUrl: 'https://www.linkedin.com/company/ted-conferences',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/6/6a/TED_wordmark.svg'
    }, {
      name: 'Unilever',
      description: "Be part of the world's most successful, purpose-led business. Work with brands that are well-loved around the world, that improve the lives of our consumers and the communities around us.",
      primaryIndustry: 'Manufacturing',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Blackfriars, London',
      country: 'United Kingdom',
      domain: 'unilever.com',
      linkedinUrl: 'https://www.linkedin.com/company/unilever',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/a/a7/Unilever_logo.svg'
    }, {
      name: 'Deloitte',
      description: 'Deloitte drives progress. Our firms around the world help clients become leaders wherever they choose to compete. Deloitte invests in outstanding people of diverse talents and backgrounds and empowers them to achieve more than they could elsewhere.',
      primaryIndustry: 'Business Consulting and Services',
      size: '10,001+ employees',
      type: 'Privately Held',
      location: 'Worldwide',
      country: '',
      domain: 'deloitte.com',
      linkedinUrl: 'https://www.linkedin.com/company/deloitte',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/56/Deloitte.svg'
    }, {
      name: 'IBM',
      description: "At IBM, we do more than work. We create. We create as technologists, developers, and engineers. We create with our partners. We create with our competitors. If you're searching for ways to make the world work better through technology and infrastructure, software and consulting, then we want to work with you.",
      primaryIndustry: 'IT Services and IT Consulting',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Armonk, New York, NY',
      country: 'United States',
      domain: 'ibm.com',
      linkedinUrl: 'https://www.linkedin.com/company/ibm',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg'
    }, {
      name: 'Apple',
      description: "We're a diverse collective of thinkers and doers, continually reimagining what's possible to help us all do what we love in new ways. And the same innovation that goes into our products also applies to our practices — strengthening our commitment to leave the world better than we found it.",
      primaryIndustry: 'Computers and Electronics Manufacturing',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Cupertino, California',
      country: 'United States',
      domain: 'apple.com',
      linkedinUrl: 'https://www.linkedin.com/company/apple',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg'
    }, {
      name: 'Tesla',
      description: "Tesla is accelerating the world's transition to sustainable abundance. To achieve our mission, we're building a world powered by solar, enabled by battery storage and transported by electric vehicles.",
      primaryIndustry: 'Motor Vehicle Manufacturing',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Austin, Texas',
      country: 'United States',
      domain: 'tesla.com',
      linkedinUrl: 'https://www.linkedin.com/company/tesla-motors',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/e/e8/Tesla_logo.png'
    }, {
      name: 'Meta',
      description: "Meta's mission is to build the future of human connection and the technology that makes it possible. Our technologies help people connect, find communities, and grow businesses.",
      primaryIndustry: 'Software Development',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Menlo Park, CA',
      country: 'United States',
      domain: 'facebook.com',
      linkedinUrl: 'https://www.linkedin.com/company/meta',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg'
    }, {
      name: 'Netflix',
      description: "Netflix is one of the world's leading entertainment services, with over 300 million paid memberships in over 190 countries enjoying TV series, films and games across a wide variety of genres and languages.",
      primaryIndustry: 'Entertainment Providers',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Los Gatos, CA',
      country: 'United States',
      domain: 'netflix.com',
      linkedinUrl: 'https://www.linkedin.com/company/netflix',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg'
    }, {
      name: 'OpenAI',
      description: 'OpenAI is an AI research and deployment company dedicated to ensuring that general-purpose artificial intelligence benefits all of humanity. AI is an extremely powerful tool that must be created with safety and human needs at its core.',
      primaryIndustry: 'Research Services',
      size: '201-500 employees',
      type: 'Partnership',
      location: 'San Francisco, CA',
      country: 'United States',
      domain: 'openai.com',
      linkedinUrl: 'https://www.linkedin.com/company/openai',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/4/4d/OpenAI_Logo.svg'
    }, {
      name: 'Accenture',
      description: "Accenture is a leading global professional services company that helps the world's leading businesses, governments and other organizations build their digital core, optimize their operations, accelerate revenue growth and enhance citizen services—creating tangible value at speed and scale.",
      primaryIndustry: 'Business Consulting and Services',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Dublin 2',
      country: 'Ireland',
      domain: 'accenture.com',
      linkedinUrl: 'https://www.linkedin.com/company/accenture',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Accenture.svg'
    }, {
      name: 'Tata Consultancy Services',
      description: "Tata Consultancy Services (TCS) is an IT services, consulting, and business solutions organization that has been partnering with many of the world's largest businesses in their transformation journeys since its inception in 1968.",
      primaryIndustry: 'IT Services and IT Consulting',
      size: '10,001+ employees',
      type: 'Public Company',
      location: 'Mumbai, Maharashtra',
      country: 'India',
      domain: 'tcs.com',
      linkedinUrl: 'https://www.linkedin.com/company/tata-consultancy-services',
      logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/9/99/Tata_Consultancy_Services_Logo_blue.svg'
    }];
    // Set company data
    setCompanies(companyData);
    setFilteredCompanies(companyData);
    // Extract unique industries, sizes, and types for filters
    const uniqueIndustries = [...new Set(companyData.map(company => company.primaryIndustry))].filter(Boolean);
    const uniqueSizes = [...new Set(companyData.map(company => company.size))].filter(Boolean);
    const uniqueTypes = [...new Set(companyData.map(company => company.type))].filter(Boolean);
    setIndustries(uniqueIndustries);
    setSizes(uniqueSizes);
    setTypes(uniqueTypes);
    // Simulate loading
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  }, []);
  // Filter companies based on search term and filters
  useEffect(() => {
    let results = companies;
    // Apply search filter
    if (searchTerm) {
      results = results.filter(company => company.name.toLowerCase().includes(searchTerm.toLowerCase()) || company.description.toLowerCase().includes(searchTerm.toLowerCase()) || company.primaryIndustry.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    // Apply industry filter
    if (selectedIndustry !== 'All') {
      results = results.filter(company => company.primaryIndustry === selectedIndustry);
    }
    // Apply size filter
    if (selectedSize !== 'All') {
      results = results.filter(company => company.size === selectedSize);
    }
    // Apply type filter
    if (selectedType !== 'All') {
      results = results.filter(company => company.type === selectedType);
    }
    setFilteredCompanies(results);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, selectedIndustry, selectedSize, selectedType, companies]);
  // Handle company selection for modal
  const handleCompanyClick = (company: Company) => {
    setModalCompany(company);
    setShowModal(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
  };
  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setModalCompany(null);
    document.body.style.overflow = ''; // Re-enable scrolling
  };
  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };
    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);
  // Animation for company cards
  useEffect(() => {
    if (!loading && companiesRef.current) {
      const cards = companiesRef.current.querySelectorAll('.company-card');
      cards.forEach((card, index) => {
        setTimeout(() => {
          card.classList.add('animate-in');
        }, 100 * index);
      });
    }
  }, [loading, filteredCompanies, currentPage]);
  // Calculate pagination
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const paginatedCompanies = filteredCompanies.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Pagination controls
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  // Handle sharing company info
  const shareToTwitter = (company, e) => {
    e.stopPropagation();
    const text = `Check out ${company.name} on Cheryl Qian's corporate entities database! ${company.description.substring(0, 100)}...`;
    const url = window.location.href;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };
  const shareToFacebook = (company, e) => {
    e.stopPropagation();
    const url = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };
  const copyCompanyLink = (company, e) => {
    e.stopPropagation();
    // Create a URL with company info in the hash
    const url = `${window.location.href.split('#')[0]}#companies?entity=${encodeURIComponent(company.name)}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }, err => {
      console.error('Could not copy text: ', err);
    });
  };
  // Toggle share options for a specific company
  const toggleShareOptions = (companyName, e) => {
    e.stopPropagation();
    if (showShareOptions === companyName) {
      setShowShareOptions(null);
    } else {
      setShowShareOptions(companyName);
    }
  };
  // Handle logo loading error
  const handleLogoError = companyName => {
    setLogoErrors(prev => ({
      ...prev,
      [companyName]: true
    }));
  };
  // Handle company highlighting
  const handleCompanyHighlight = (companyName: string | null) => {
    setHighlightedCompany(companyName);
  };
  return <section id="companies" className="w-full py-24 bg-[#060a10] relative" data-id="element-197">
      <style jsx global data-id="element-198">{`
        @keyframes company-reveal {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .company-card {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.4s ease-out;
          transform-style: preserve-3d;
        }
        .company-card.animate-in {
          animation: company-reveal 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)
            forwards;
        }
        .company-card:hover {
          border-color: rgba(111, 248, 240, 0.5);
        }
        .company-card.active {
          border-color: rgba(111, 248, 240, 0.7);
          box-shadow: 0 0 20px rgba(111, 248, 240, 0.2);
        }
        .company-card.highlighted {
          transform: scale(1.03);
          z-index: 10;
          border-color: rgba(111, 248, 240, 0.7);
          box-shadow: 0 0 25px rgba(111, 248, 240, 0.25);
        }
        .company-card.dimmed {
          opacity: 0.5;
          filter: saturate(0.7) brightness(0.8);
          transform: scale(0.98);
        }
        .company-logo {
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          width: 60px;
          height: 60px;
          border-radius: 12px;
          margin-left: 10px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        .company-card:hover .company-logo,
        .company-card.highlighted .company-logo {
          transform: scale(1.05);
        }
        .company-logo img {
          max-width: 80%;
          max-height: 80%;
          object-fit: contain;
          transition: all 0.3s ease;
        }
        .company-logo.has-logo {
          background: rgba(255, 255, 255, 0.95);
          padding: 10px;
          border: none;
        }
        .company-logo.has-logo:hover img,
        .company-card.highlighted .company-logo.has-logo img {
          transform: scale(1.1);
        }
        .company-description {
          max-height: 0;
          overflow: hidden;
          transition:
            max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
            opacity 0.3s ease;
          opacity: 0;
        }
        .company-description.active {
          max-height: 1000px;
          opacity: 1;
        }
        .filter-dropdown {
          transition: all 0.3s ease;
        }
        .filter-dropdown:hover {
          border-color: rgba(111, 248, 240, 0.5);
        }
        .search-input {
          transition: all 0.3s ease;
        }
        .search-input:hover,
        .search-input:focus {
          border-color: rgba(111, 248, 240, 0.5);
          box-shadow: 0 0 10px rgba(111, 248, 240, 0.2);
        }
        .filter-badge {
          transition: all 0.3s ease;
        }
        .filter-badge:hover {
          background: rgba(111, 248, 240, 0.2);
        }
        .scan-effect {
          position: absolute;
          height: 5px;
          width: 100%;
          background: linear-gradient(
            to right,
            rgba(111, 248, 240, 0) 0%,
            rgba(111, 248, 240, 0.5) 50%,
            rgba(111, 248, 240, 0) 100%
          );
          animation: scan 3s linear infinite;
          opacity: 0.7;
        }
        @keyframes scan {
          0% {
            top: 0;
          }
          100% {
            top: 100%;
          }
        }
        .interface-corner {
          position: absolute;
          width: 10px;
          height: 10px;
          border-color: rgba(111, 248, 240, 0.5);
          transition: all 0.3s ease;
        }
        .corner-tl {
          top: 0;
          left: 0;
          border-top: 1px solid;
          border-left: 1px solid;
        }
        .corner-tr {
          top: 0;
          right: 0;
          border-top: 1px solid;
          border-right: 1px solid;
        }
        .corner-bl {
          bottom: 0;
          left: 0;
          border-bottom: 1px solid;
          border-left: 1px solid;
        }
        .corner-br {
          bottom: 0;
          right: 0;
          border-bottom: 1px solid;
          border-right: 1px solid;
        }
        .company-card:hover .interface-corner {
          width: 15px;
          height: 15px;
        }
        .company-card.active .interface-corner {
          width: 20px;
          height: 20px;
        }
        .company-stat {
          transition: all 0.3s ease;
        }
        .company-card:hover .company-stat {
          color: rgba(111, 248, 240, 1);
        }
        .linkedin-button {
          transition: all 0.3s ease;
          overflow: hidden;
          position: relative;
        }
        .linkedin-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(111, 248, 240, 0.2),
            transparent
          );
          transition: all 0.6s ease;
        }
        .linkedin-button:hover::before {
          left: 100%;
        }
        .data-point {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
        }
        .data-point-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 2px;
          background: rgba(111, 248, 240, 0.1);
          color: #6ff8f0;
        }
        .loading-skeleton {
          background: linear-gradient(
            90deg,
            rgba(30, 41, 59, 0.8) 0%,
            rgba(40, 53, 74, 0.8) 50%,
            rgba(30, 41, 59, 0.8) 100%
          );
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
        }
        @keyframes skeleton-loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
        .share-options {
          position: absolute;
          top: 100%;
          right: 0;
          background: rgba(3, 5, 8, 0.95);
          border: 1px solid rgba(111, 248, 240, 0.3);
          border-radius: 8px;
          padding: 10px;
          margin-top: 8px;
          z-index: 50;
          box-shadow: 0 0 30px rgba(111, 248, 240, 0.2);
          backdrop-filter: blur(10px);
          width: 180px;
        }
        .share-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          width: 100%;
          border-radius: 4px;
          background: transparent;
          color: #a0c0e0;
          border: none;
          transition: all 0.3s ease;
          text-align: left;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
        }
        .share-button:hover {
          background: rgba(111, 248, 240, 0.1);
          color: #6ff8f0;
        }
        .share-button-twitter {
          color: #1da1f2;
        }
        .share-button-facebook {
          color: #4267b2;
        }
        .share-button-copy {
          color: #a0c0e0;
        }
        .success-message {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(111, 248, 240, 0.2);
          border: 1px solid rgba(111, 248, 240, 0.5);
          border-radius: 4px;
          padding: 8px 16px;
          color: #6ff8f0;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          gap: 8px;
          animation: fadeInOut 2s forwards;
          z-index: 100;
        }
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          20% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          80% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
        }
        .company-actions {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }
        .company-action-button {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          gap: 6px;
          padding: 6px 10px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.7rem;
          background: rgba(10, 16, 24, 0.8);
          border: 1px solid rgba(26, 42, 64, 0.5);
          border-radius: 4px;
          color: #8a9cb9;
          transition: all 0.3s ease;
        }
        .company-action-button:hover {
          background: rgba(111, 248, 240, 0.1);
          border-color: rgba(111, 248, 240, 0.3);
          color: #6ff8f0;
        }
        /* Modal Styles */
        .company-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(3, 5, 8, 0.9);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease-out;
        }
        .company-modal {
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          background: #060a10;
          border: 1px solid rgba(111, 248, 240, 0.3);
          border-radius: 8px;
          position: relative;
          animation: modalSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .company-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid rgba(111, 248, 240, 0.2);
          position: sticky;
          top: 0;
          background: #060a10;
          z-index: 10;
        }
        .company-modal-close {
          background: rgba(111, 248, 240, 0.1);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6ff8f0;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .company-modal-close:hover {
          background: rgba(111, 248, 240, 0.2);
          transform: rotate(90deg);
        }
        .company-modal-content {
          padding: 20px;
        }
        .company-modal-footer {
          padding: 20px;
          border-top: 1px solid rgba(111, 248, 240, 0.2);
          display: flex;
          justify-content: space-between;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        /* Pagination Styles */
        .pagination-controls {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-top: 30px;
        }
        .pagination-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(10, 16, 24, 0.8);
          border: 1px solid rgba(111, 248, 240, 0.3);
          color: #6ff8f0;
          transition: all 0.3s ease;
        }
        .pagination-button:hover:not(:disabled) {
          background: rgba(111, 248, 240, 0.2);
          transform: translateY(-2px);
        }
        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .pagination-info {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.85rem;
          color: #8a9cb9;
        }
        .modal-logo {
          width: 140px;
          height: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          padding: 20px;
          margin-right: 30px;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        }
        .modal-logo img {
          max-width: 80%;
          max-height: 80%;
          object-fit: contain;
        }
        .modal-header-content {
          display: flex;
          align-items: center;
          flex: 1;
        }
        .modal-company-info {
          flex: 1;
        }
        @media (max-width: 640px) {
          .modal-header-content {
            flex-direction: column;
            align-items: flex-start;
          }
          .modal-logo {
            margin-right: 0;
            margin-bottom: 15px;
          }
        }
      `}</style>
      {/* Background effects */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay" data-id="element-199"></div>
      {/* Copy Success Message */}
      {copySuccess && <div className="success-message" data-id="element-200">
          <CheckIcon size={14} data-id="element-201" />
          Link copied to clipboard
        </div>}
      <div className="container mx-auto px-6 relative z-10" data-id="element-202">
        <div className="text-center mb-16" data-id="element-203">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gradient font-heading animate-fade-in-up" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-204">
            CORPORATE ENTITIES
          </h2>
          <p className="text-[#8a9cb9] max-w-2xl mx-auto font-mono animate-fade-in-up" style={{
          animationDelay: '0.2s'
        }} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-205">
            Mapped neural networks of global corporate influence. Entities that
            shape technological evolution.
          </p>
        </div>
        {/* Search and filters */}
        <div className="mb-12 glass p-6 rounded-lg border border-[#1a2a40]/50 relative" data-id="element-206">
          {/* Interface corners */}
          <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#6ff8f0]/50" data-id="element-207"></span>
          <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#6ff8f0]/50" data-id="element-208"></span>
          <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#6ff8f0]/50" data-id="element-209"></span>
          <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#6ff8f0]/50" data-id="element-210"></span>
          {/* Search bar */}
          <div className="relative mb-6" data-id="element-211">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-212">
              <SearchIcon size={18} className="text-[#6ff8f0]" data-id="element-213" />
            </div>
            <input type="text" className="search-input w-full pl-10 pr-4 py-3 bg-[#0a1018] border border-[#1a2a40]/50 rounded-md text-[#e0e6f0] font-mono focus:outline-none focus:ring-1 focus:ring-[#6ff8f0]/50" placeholder="SEARCH ENTITIES..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-214" />
          </div>
          {/* Filters */}
          <div className="flex flex-wrap gap-4" data-id="element-215">
            <div className="relative" data-id="element-216">
              <label className="block text-[#8a9cb9] text-xs font-mono mb-2" data-id="element-217">
                INDUSTRY
              </label>
              <select className="filter-dropdown w-full md:w-48 py-2 px-3 bg-[#0a1018] border border-[#1a2a40]/50 rounded-md text-[#e0e6f0] font-mono focus:outline-none focus:ring-1 focus:ring-[#6ff8f0]/50" value={selectedIndustry} onChange={e => setSelectedIndustry(e.target.value)} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-218">
                <option value="All" data-id="element-219">All Industries</option>
                {industries.map(industry => <option key={industry} value={industry} data-id="element-220">
                    {industry}
                  </option>)}
              </select>
            </div>
            <div className="relative" data-id="element-221">
              <label className="block text-[#8a9cb9] text-xs font-mono mb-2" data-id="element-222">
                COMPANY SIZE
              </label>
              <select className="filter-dropdown w-full md:w-48 py-2 px-3 bg-[#0a1018] border border-[#1a2a40]/50 rounded-md text-[#e0e6f0] font-mono focus:outline-none focus:ring-1 focus:ring-[#6ff8f0]/50" value={selectedSize} onChange={e => setSelectedSize(e.target.value)} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-223">
                <option value="All" data-id="element-224">All Sizes</option>
                {sizes.map(size => <option key={size} value={size} data-id="element-225">
                    {size}
                  </option>)}
              </select>
            </div>
            <div className="relative" data-id="element-226">
              <label className="block text-[#8a9cb9] text-xs font-mono mb-2" data-id="element-227">
                COMPANY TYPE
              </label>
              <select className="filter-dropdown w-full md:w-48 py-2 px-3 bg-[#0a1018] border border-[#1a2a40]/50 rounded-md text-[#e0e6f0] font-mono focus:outline-none focus:ring-1 focus:ring-[#6ff8f0]/50" value={selectedType} onChange={e => setSelectedType(e.target.value)} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-228">
                <option value="All" data-id="element-229">All Types</option>
                {types.map(type => <option key={type} value={type} data-id="element-230">
                    {type}
                  </option>)}
              </select>
            </div>
          </div>
          {/* Active filters display */}
          {(selectedIndustry !== 'All' || selectedSize !== 'All' || selectedType !== 'All' || searchTerm) && <div className="mt-4 flex flex-wrap gap-2" data-id="element-231">
              {searchTerm && <span className="filter-badge px-3 py-1 bg-[#0a1018] border border-[#6ff8f0]/30 rounded-full text-[#6ff8f0] text-xs font-mono flex items-center gap-1" data-id="element-232">
                  <SearchIcon size={12} data-id="element-233" />
                  {searchTerm}
                </span>}
              {selectedIndustry !== 'All' && <span className="filter-badge px-3 py-1 bg-[#0a1018] border border-[#6ff8f0]/30 rounded-full text-[#6ff8f0] text-xs font-mono flex items-center gap-1" data-id="element-234">
                  <BriefcaseIcon size={12} data-id="element-235" />
                  {selectedIndustry}
                </span>}
              {selectedSize !== 'All' && <span className="filter-badge px-3 py-1 bg-[#0a1018] border border-[#6ff8f0]/30 rounded-full text-[#6ff8f0] text-xs font-mono flex items-center gap-1" data-id="element-236">
                  <UsersIcon size={12} data-id="element-237" />
                  {selectedSize}
                </span>}
              {selectedType !== 'All' && <span className="filter-badge px-3 py-1 bg-[#0a1018] border border-[#6ff8f0]/30 rounded-full text-[#6ff8f0] text-xs font-mono flex items-center gap-1" data-id="element-238">
                  <BuildingIcon size={12} data-id="element-239" />
                  {selectedType}
                </span>}
            </div>}
        </div>
        {/* Results count */}
        <div className="mb-6 flex justify-between items-center" data-id="element-240">
          <div className="text-[#8a9cb9] font-mono text-sm" data-id="element-241">
            <span className="text-[#6ff8f0]" data-id="element-242">{filteredCompanies.length}</span>{' '}
            entities found
          </div>
          {loading && <div className="text-[#8a9cb9] font-mono text-sm flex items-center gap-2" data-id="element-243">
              <div className="w-3 h-3 rounded-full bg-[#6ff8f0] animate-pulse" data-id="element-244"></div>
              Processing data...
            </div>}
        </div>
        {/* Company grid */}
        {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-245">
            {[...Array(6)].map((_, i) => <div key={i} className="loading-skeleton h-64 rounded-lg" onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => onCursorChange('default')} data-id="element-246"></div>)}
          </div> : <>
            <div ref={companiesRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-247">
              {filteredCompanies.length > 0 ? paginatedCompanies.map((company, index) => <div key={index} id={`company-${company.name.replace(/\s+/g, '-')}`} className={`company-card glass border border-[#1a2a40]/50 rounded-lg overflow-hidden relative cursor-pointer
                      ${highlightedCompany && highlightedCompany !== company.name ? 'dimmed' : ''}
                      ${highlightedCompany === company.name ? 'highlighted' : ''}
                    `} onClick={() => handleCompanyClick(company)} onMouseEnter={() => {
            onCursorChange('image');
            handleCompanyHighlight(company.name);
          }} onMouseLeave={() => {
            onCursorChange('default');
            handleCompanyHighlight(null);
          }} data-id="element-248">
                    {/* Interface corners */}
                    <div className="interface-corner corner-tl" data-id="element-249"></div>
                    <div className="interface-corner corner-tr" data-id="element-250"></div>
                    <div className="interface-corner corner-bl" data-id="element-251"></div>
                    <div className="interface-corner corner-br" data-id="element-252"></div>
                    {/* Scan effect */}
                    <div className={`scan-effect ${highlightedCompany === company.name ? 'opacity-100' : 'opacity-50'}`} data-id="element-253"></div>
                    {/* Company header */}
                    <div className="p-5" data-id="element-254">
                      <div className="flex items-center justify-between mb-4" data-id="element-255">
                        <h3 className="text-xl text-[#e0e6f0] font-bold font-heading" data-id="element-256">
                          {company.name}
                        </h3>
                        <div className={`company-logo ${!logoErrors[company.name] && company.logoUrl ? 'has-logo' : 'bg-[#0a1018]'}`} data-id="element-257">
                          {!logoErrors[company.name] && company.logoUrl ? <img src={company.logoUrl} alt={`${company.name} logo`} onError={() => handleLogoError(company.name)} data-id="element-258" /> : <div className="text-[#6ff8f0] font-bold text-xl" data-id="element-259">
                              {company.domain.split('.')[0].charAt(0).toUpperCase()}
                            </div>}
                        </div>
                      </div>
                      {/* Company meta */}
                      <div className="flex flex-wrap gap-y-2 mb-4" data-id="element-260">
                        <div className="w-full data-point" data-id="element-261">
                          <div className="data-point-icon" data-id="element-262">
                            <BriefcaseIcon size={12} data-id="element-263" />
                          </div>
                          <span className="text-[#8a9cb9]" data-id="element-264">
                            {company.primaryIndustry || 'Unknown Industry'}
                          </span>
                        </div>
                        <div className="w-full data-point" data-id="element-265">
                          <div className="data-point-icon" data-id="element-266">
                            <MapPinIcon size={12} data-id="element-267" />
                          </div>
                          <span className="text-[#8a9cb9]" data-id="element-268">
                            {company.location}
                            {company.country && `, ${company.country}`}
                          </span>
                        </div>
                      </div>
                      {/* Company stats */}
                      <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-269">
                        <div className="company-stat bg-[#0a1018]/80 p-2 rounded border border-[#1a2a40]/30 text-center" data-id="element-270">
                          <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-271">
                            TYPE
                          </div>
                          <div className="text-[#e0e6f0] text-sm font-mono truncate" data-id="element-272">
                            {company.type || 'Unknown'}
                          </div>
                        </div>
                        <div className="company-stat bg-[#0a1018]/80 p-2 rounded border border-[#1a2a40]/30 text-center" data-id="element-273">
                          <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-274">
                            SIZE
                          </div>
                          <div className="text-[#e0e6f0] text-sm font-mono truncate" data-id="element-275">
                            {company.size || 'Unknown'}
                          </div>
                        </div>
                      </div>
                      {/* Preview of description */}
                      <p className="text-[#8a9cb9] text-sm font-mono line-clamp-2" data-id="element-276">
                        {company.description}
                      </p>
                      {/* View details indicator */}
                      <div className="mt-4 text-center" data-id="element-277">
                        <span className="text-xs text-[#6ff8f0] font-mono bg-[#6ff8f0]/10 px-3 py-1 rounded-full" data-id="element-278">
                          VIEW DETAILS
                        </span>
                      </div>
                    </div>
                  </div>) : <div className="col-span-3 text-center py-12" data-id="element-279">
                  <div className="mb-4" data-id="element-280">
                    <SearchIcon size={48} className="text-[#1a2a40] mx-auto" data-id="element-281" />
                  </div>
                  <h3 className="text-xl text-[#e0e6f0] font-bold mb-2" data-id="element-282">
                    No entities found
                  </h3>
                  <p className="text-[#8a9cb9] font-mono" data-id="element-283">
                    Try adjusting your search criteria
                  </p>
                </div>}
            </div>
            {/* Pagination Controls */}
            {filteredCompanies.length > itemsPerPage && <div className="pagination-controls" data-id="element-284">
                <button className="pagination-button" onClick={goToPrevPage} disabled={currentPage === 1} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-285">
                  <ChevronLeftIcon size={20} data-id="element-286" />
                </button>
                <div className="pagination-info" data-id="element-287">
                  Page <span className="text-[#6ff8f0]" data-id="element-288">{currentPage}</span> of{' '}
                  <span className="text-[#6ff8f0]" data-id="element-289">{totalPages}</span>
                </div>
                <button className="pagination-button" onClick={goToNextPage} disabled={currentPage === totalPages} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-290">
                  <ChevronRightIcon size={20} data-id="element-291" />
                </button>
              </div>}
          </>}
      </div>
      {/* Company Detail Modal */}
      {showModal && modalCompany && <div className="company-modal-overlay" data-id="element-292">
          <div className="company-modal" ref={modalRef} data-id="element-293">
            <div className="company-modal-header" data-id="element-294">
              <div className="modal-header-content" data-id="element-295">
                {/* Company Logo in Modal */}
                <div className="modal-logo" data-id="element-296">
                  {!logoErrors[modalCompany.name] && modalCompany.logoUrl ? <img src={modalCompany.logoUrl} alt={`${modalCompany.name} logo`} onError={() => handleLogoError(modalCompany.name)} data-id="element-297" /> : <div className="text-[#0a1018] font-bold text-3xl flex items-center justify-center w-full h-full" data-id="element-298">
                      {modalCompany.domain.split('.')[0].charAt(0).toUpperCase()}
                    </div>}
                </div>
                <div className="modal-company-info" data-id="element-299">
                  <h3 className="text-2xl text-[#e0e6f0] font-bold font-heading" data-id="element-300">
                    {modalCompany.name}
                  </h3>
                  <p className="text-[#8a9cb9] text-sm font-mono" data-id="element-301">
                    {modalCompany.domain}
                  </p>
                </div>
              </div>
              <button className="company-modal-close" onClick={closeModal} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-302">
                <XIcon size={18} data-id="element-303" />
              </button>
            </div>
            <div className="company-modal-content" data-id="element-304">
              <div className="mb-6 p-4 glass rounded-lg border border-[#1a2a40]/50" data-id="element-305">
                <h4 className="text-[#6ff8f0] font-mono text-sm mb-2" data-id="element-306">
                  ENTITY PROFILE
                </h4>
                <p className="text-[#e0e6f0] font-mono" data-id="element-307">
                  {modalCompany.description}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-308">
                <div className="glass p-4 rounded-lg border border-[#1a2a40]/50" data-id="element-309">
                  <h4 className="text-[#6ff8f0] font-mono text-sm mb-4" data-id="element-310">
                    ENTITY DETAILS
                  </h4>
                  <div className="space-y-3" data-id="element-311">
                    <div className="data-point" data-id="element-312">
                      <div className="data-point-icon" data-id="element-313">
                        <BriefcaseIcon size={12} data-id="element-314" />
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-315">Industry:</span>
                      <span className="text-[#e0e6f0] ml-auto" data-id="element-316">
                        {modalCompany.primaryIndustry}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-317">
                      <div className="data-point-icon" data-id="element-318">
                        <BuildingIcon size={12} data-id="element-319" />
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-320">Type:</span>
                      <span className="text-[#e0e6f0] ml-auto" data-id="element-321">
                        {modalCompany.type}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-322">
                      <div className="data-point-icon" data-id="element-323">
                        <UsersIcon size={12} data-id="element-324" />
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-325">Size:</span>
                      <span className="text-[#e0e6f0] ml-auto" data-id="element-326">
                        {modalCompany.size}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="glass p-4 rounded-lg border border-[#1a2a40]/50" data-id="element-327">
                  <h4 className="text-[#6ff8f0] font-mono text-sm mb-4" data-id="element-328">
                    LOCATION DATA
                  </h4>
                  <div className="space-y-3" data-id="element-329">
                    <div className="data-point" data-id="element-330">
                      <div className="data-point-icon" data-id="element-331">
                        <MapPinIcon size={12} data-id="element-332" />
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-333">Headquarters:</span>
                      <span className="text-[#e0e6f0] ml-auto" data-id="element-334">
                        {modalCompany.location}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-335">
                      <div className="data-point-icon" data-id="element-336">
                        <GlobeIcon size={12} data-id="element-337" />
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-338">Country:</span>
                      <span className="text-[#e0e6f0] ml-auto" data-id="element-339">
                        {modalCompany.country || 'Global'}
                      </span>
                    </div>
                    <div className="data-point" data-id="element-340">
                      <div className="data-point-icon" data-id="element-341">
                        <GlobeIcon size={12} data-id="element-342" />
                      </div>
                      <span className="text-[#8a9cb9]" data-id="element-343">Domain:</span>
                      <span className="text-[#e0e6f0] ml-auto" data-id="element-344">
                        {modalCompany.domain}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="glass p-4 rounded-lg border border-[#1a2a40]/50 mb-6" data-id="element-345">
                <h4 className="text-[#6ff8f0] font-mono text-sm mb-4" data-id="element-346">
                  ENTITY ANALYSIS
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-347">
                  <div className="bg-[#0a1018]/80 p-3 rounded border border-[#1a2a40]/30 text-center" data-id="element-348">
                    <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-349">
                      DATA QUALITY
                    </div>
                    <div className="text-[#6ff8f0] text-lg font-mono" data-id="element-350">
                      {Math.floor(Math.random() * 30) + 70}%
                    </div>
                  </div>
                  <div className="bg-[#0a1018]/80 p-3 rounded border border-[#1a2a40]/30 text-center" data-id="element-351">
                    <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-352">
                      INFLUENCE
                    </div>
                    <div className="text-[#6ff8f0] text-lg font-mono" data-id="element-353">
                      {Math.floor(Math.random() * 30) + 70}/100
                    </div>
                  </div>
                  <div className="bg-[#0a1018]/80 p-3 rounded border border-[#1a2a40]/30 text-center" data-id="element-354">
                    <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-355">
                      TECH SCORE
                    </div>
                    <div className="text-[#6ff8f0] text-lg font-mono" data-id="element-356">
                      {Math.floor(Math.random() * 30) + 70}/100
                    </div>
                  </div>
                  <div className="bg-[#0a1018]/80 p-3 rounded border border-[#1a2a40]/30 text-center" data-id="element-357">
                    <div className="text-[#8a9cb9] text-xs font-mono" data-id="element-358">
                      STATUS
                    </div>
                    <div className="text-[#6ff8f0] text-lg font-mono" data-id="element-359">
                      {['ACTIVE', 'STABLE', 'EVOLVING'][Math.floor(Math.random() * 3)]}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="company-modal-footer" data-id="element-360">
              {modalCompany.linkedinUrl && <a href={modalCompany.linkedinUrl} target="_blank" rel="noopener noreferrer" className="linkedin-button px-4 py-2 bg-[#0a1018] border border-[#6ff8f0]/30 rounded text-[#6ff8f0] text-sm font-mono flex items-center gap-2" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-361">
                  <ExternalLinkIcon size={16} data-id="element-362" />
                  View on LinkedIn
                </a>}
              <div className="flex gap-2" data-id="element-363">
                <button className="px-4 py-2 bg-[#0a1018] border border-[#6ff8f0]/30 rounded text-[#6ff8f0] text-sm font-mono" onClick={e => shareToTwitter(modalCompany, e)} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-364">
                  <TwitterIcon size={16} data-id="element-365" />
                </button>
                <button className="px-4 py-2 bg-[#0a1018] border border-[#6ff8f0]/30 rounded text-[#6ff8f0] text-sm font-mono" onClick={e => shareToFacebook(modalCompany, e)} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-366">
                  <FacebookIcon size={16} data-id="element-367" />
                </button>
                <button className="px-4 py-2 bg-[#0a1018] border border-[#6ff8f0]/30 rounded text-[#6ff8f0] text-sm font-mono" onClick={e => copyCompanyLink(modalCompany, e)} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-368">
                  <CopyIcon size={16} data-id="element-369" />
                </button>
              </div>
            </div>
          </div>
        </div>}
    </section>;
};